import { z } from 'zod';

/**
 * Dale Agents (Ultra): the intelligence advisors, now preparing the work.
 * Each proposal is versioned; every edit, approval or dismissal names the
 * version it saw, and the server answers 409 if someone changed it since.
 * Shapes follow docs/backend-api-contract.v1.md, "Dale Agents".
 */

export const AgentIdSchema = z.enum(['direccion', 'marketing', 'finanzas', 'operaciones']);
export type AgentId = z.infer<typeof AgentIdSchema>;

export const AgentStatusSchema = z.enum(['available', 'working', 'needs_review']);
export type AgentStatus = z.infer<typeof AgentStatusSchema>;

export const AgentSchema = z.object({
  id: AgentIdSchema,
  name: z.string(),
  title: z.string(),
  role: z.string(),
  permission: z.string(),
  status: AgentStatusSchema,
  task: z.string().optional(),
});
export type Agent = z.infer<typeof AgentSchema>;

export const OfferDraftSchema = z.object({
  kind: z.literal('offer'),
  title: z.string(),
  description: z.string(),
  percentOff: z.number().int(),
  validDays: z.array(z.number().int().min(0).max(6)),
  startsAt: z.string(),
  validUntil: z.string(),
});
export type OfferDraft = z.infer<typeof OfferDraftSchema>;

export const MessageDraftSchema = z.object({
  kind: z.literal('message'),
  text: z.string(),
  skip: z.array(z.string()),
});
export type MessageDraft = z.infer<typeof MessageDraftSchema>;

export const ProposalDraftSchema = z.discriminatedUnion('kind', [OfferDraftSchema, MessageDraftSchema]);
export type ProposalDraft = z.infer<typeof ProposalDraftSchema>;

export const RecipientSchema = z.object({
  customerId: z.string(),
  name: z.string(),
  phone: z.string().nullable(),
  detail: z.string(),
  bookingId: z.string().optional(),
});
export type Recipient = z.infer<typeof RecipientSchema>;

export const ProposalKindSchema = z.enum(['offer', 'win_back', 'confirm_visits']);
export type ProposalKind = z.infer<typeof ProposalKindSchema>;

export const ProposalSchema = z.object({
  id: z.string(),
  agentId: AgentIdSchema,
  kind: ProposalKindSchema,
  status: z.enum(['pending', 'done', 'dismissed']),
  version: z.number().int(),
  title: z.string(),
  reason: z.string(),
  evidence: z.array(z.object({ label: z.string(), value: z.string() })),
  draft: ProposalDraftSchema,
  recipients: z.array(RecipientSchema).optional(),
  result: z
    .object({ summary: z.string(), offerId: z.string().optional(), measured: z.string().optional() })
    .optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
  decidedAt: z.string().optional(),
});
export type Proposal = z.infer<typeof ProposalSchema>;

export const AgentsResponseSchema = z.object({
  data: z.object({
    generatedAt: z.string(),
    agents: z.array(AgentSchema),
    proposals: z.array(ProposalSchema),
  }),
});
export type AgentsBoard = z.infer<typeof AgentsResponseSchema>['data'];

export const ProposalResponseSchema = z.object({ data: ProposalSchema });
